import { useAuth } from '../../../auth';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { authHeaders, jsonAuthHeaders } from '../../../shared/authHeaders';
import { formatShortDateTime } from '../../../shared/dates';
import type { AdminPlan, AdminPlanStatus, AdminPlanType } from '@modelcast/types';

type PlanDraft = {
  title: string;
  summary: string;
  body: string;
  planType: AdminPlanType;
};

const STATUS_FILTERS: Array<{ value: AdminPlanStatus; label: string }> = [
  { value: 'draft', label: 'Draft' },
  { value: 'needs_clarification', label: 'Needs clarification' },
  { value: 'approved', label: 'Approved' },
  { value: 'running', label: 'Running' },
  { value: 'blocked', label: 'Blocked' },
  { value: 'failed', label: 'Failed' },
  { value: 'implemented', label: 'Implemented' },
];

export const STATUS_FILTER_STORAGE_KEY = 'drawclub.admin.plans.statusFilters';

function labelize(value: string) {
  return value.replaceAll('_', ' ');
}

function statusClass(status: string) {
  switch (status) {
    case 'approved':
    case 'queued':
    case 'running':
      return 'border-blue-500/25 bg-blue-500/10 text-blue-700';
    case 'implemented':
    case 'succeeded':
      return 'border-ok/25 bg-ok/10 text-ok';
    case 'blocked':
    case 'needs_clarification':
      return 'border-warn/25 bg-warn/10 text-warn';
    case 'failed':
      return 'border-red-500/25 bg-red-500/10 text-red-600';
    default:
      return 'border-line bg-panel text-text-dim';
  }
}

function makeDraft(plan: AdminPlan): PlanDraft {
  return {
    title: plan.title,
    summary: plan.summary ?? '',
    body: plan.body,
    planType: plan.planType,
  };
}

function isAdminPlanStatus(value: unknown): value is AdminPlanStatus {
  return STATUS_FILTERS.some((filter) => filter.value === value);
}

function readRequestedPlanId(): string | null {
  if (typeof window === 'undefined') return null;
  return new URLSearchParams(window.location.search).get('planId');
}

function readUrlStatusFilters(): AdminPlanStatus[] {
  if (typeof window === 'undefined') return [];

  const params = new URLSearchParams(window.location.search);
  const statuses = params.getAll('status').filter(isAdminPlanStatus);
  return STATUS_FILTERS
    .map((filter) => filter.value)
    .filter((status) => statuses.includes(status));
}

export function readSavedStatusFilters(): AdminPlanStatus[] {
  if (typeof window === 'undefined') return [];

  try {
    const rawValue = window.localStorage.getItem(STATUS_FILTER_STORAGE_KEY);
    if (!rawValue) return [];

    const parsedValue = JSON.parse(rawValue) as unknown;
    if (!Array.isArray(parsedValue)) return [];

    const savedStatuses = new Set(parsedValue.filter(isAdminPlanStatus));
    return STATUS_FILTERS
      .map((filter) => filter.value)
      .filter((status) => savedStatuses.has(status));
  } catch {
    return [];
  }
}

export function saveStatusFilters(statusFilters: AdminPlanStatus[]) {
  if (typeof window === 'undefined') return;

  if (statusFilters.length === 0) {
    window.localStorage.removeItem(STATUS_FILTER_STORAGE_KEY);
    return;
  }

  window.localStorage.setItem(STATUS_FILTER_STORAGE_KEY, JSON.stringify(statusFilters));
}

export function PlansPage() {
  const { getToken } = useAuth();
  const requestedPlanId = useRef(readRequestedPlanId());
  const [plans, setPlans] = useState<AdminPlan[]>([]);
  const [selectedPlanId, setSelectedPlanId] = useState<string | null>(null);
  const [draft, setDraft] = useState<PlanDraft | null>(null);
  const [isCreatingNew, setIsCreatingNew] = useState(false);
  const [statusFilters, setStatusFilters] = useState<AdminPlanStatus[]>(() => {
    const urlFilters = readUrlStatusFilters();
    return urlFilters.length > 0 ? urlFilters : readSavedStatusFilters();
  });
  const [isLoading, setIsLoading] = useState(true);
  const [isSaving, setIsSaving] = useState(false);
  const [isApproving, setIsApproving] = useState(false);
  const [isDeleting, setIsDeleting] = useState(false);
  const [isRestoringDeletedPlan, setIsRestoringDeletedPlan] = useState(false);
  const [isAddingNote, setIsAddingNote] = useState(false);
  const [noteDraft, setNoteDraft] = useState('');
  const [planPendingDelete, setPlanPendingDelete] = useState<AdminPlan | null>(null);
  const [deletedPlanForUndo, setDeletedPlanForUndo] = useState<AdminPlan | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [notice, setNotice] = useState<string | null>(null);

  const loadPlans = useCallback(async () => {
    setIsLoading(true);
    setError(null);
    try {
      const token = await getToken();
      const params = new URLSearchParams();
      statusFilters.forEach((status) => params.append('status', status));
      const response = await fetch(`/api/admin/plans?${params}`, {
        headers: authHeaders(token),
      });
      const data = await response.json();
      if (!response.ok) {
        throw new Error(data.error || 'Failed to load plans');
      }

      setPlans(data.plans);
      setSelectedPlanId((current) => {
        if (requestedPlanId.current && data.plans.some((plan: AdminPlan) => plan.id === requestedPlanId.current)) {
          return requestedPlanId.current;
        }
        if (current && data.plans.some((plan: AdminPlan) => plan.id === current)) return current;
        return data.plans[0]?.id ?? null;
      });
    } catch (err) {
      console.error('Error loading plans:', err);
      setError(err instanceof Error ? err.message : 'Failed to load plans');
    } finally {
      setIsLoading(false);
    }
  }, [getToken, statusFilters]);

  useEffect(() => {
    void loadPlans();
  }, [loadPlans]);

  useEffect(() => {
    saveStatusFilters(statusFilters);
  }, [statusFilters]);

  const selectedPlan = useMemo(
    () => plans.find((plan) => plan.id === selectedPlanId) ?? null,
    [plans, selectedPlanId]
  );

  useEffect(() => {
    if (isCreatingNew) return;
    setDraft(selectedPlan ? makeDraft(selectedPlan) : null);
  }, [isCreatingNew, selectedPlan]);

  useEffect(() => {
    setNoteDraft('');
  }, [selectedPlanId]);

  const hasChanges = isCreatingNew
    ? Boolean(draft && (
      draft.title.trim() ||
      draft.summary.trim() ||
      draft.body.trim() ||
      draft.planType !== 'product'
    ))
    : Boolean(selectedPlan && draft && (
      draft.title !== selectedPlan.title ||
      draft.summary !== (selectedPlan.summary ?? '') ||
      draft.body !== selectedPlan.body ||
      draft.planType !== selectedPlan.planType
    ));
  const canSaveDraft = Boolean(draft?.title.trim() && draft?.body.trim());
  const canChangeSelectedPlanStatus = Boolean(
    selectedPlan && !['running', 'queued'].includes(selectedPlan.status)
  );
  const canRequestClarification = Boolean(
    canChangeSelectedPlanStatus && noteDraft.trim()
  );
  const deleteDisabledReason = selectedPlan
    ? hasChanges
      ? 'Save or reset changes before deleting this plan.'
      : ['running', 'queued'].includes(selectedPlan.status)
        ? 'Cannot delete while a plan is running or queued.'
        : null
    : null;

  useEffect(() => {
    if (!planPendingDelete) return;

    function closeOnEscape(event: KeyboardEvent) {
      if (event.key === 'Escape') {
        setPlanPendingDelete(null);
      }
    }

    window.addEventListener('keydown', closeOnEscape);
    return () => window.removeEventListener('keydown', closeOnEscape);
  }, [planPendingDelete]);

  function startNewPlan() {
    setIsCreatingNew(true);
    setSelectedPlanId(null);
    setStatusFilters([]);
    setDraft({
      title: '',
      summary: '',
      body: '',
      planType: 'product',
    });
    setNoteDraft('');
    setError(null);
    setNotice(null);
  }

  function cancelNewPlan() {
    setIsCreatingNew(false);
    setDraft(selectedPlan ? makeDraft(selectedPlan) : null);
  }

  function toggleStatusFilter(status: AdminPlanStatus) {
    setStatusFilters((current) => current.includes(status)
      ? current.filter((item) => item !== status)
      : [...current, status]);
  }

  async function savePlan() {
    if (isCreatingNew) {
      await createPlan();
      return;
    }
    if (!selectedPlan || !draft) return;

    setIsSaving(true);
    setError(null);
    setNotice(null);
    try {
      const token = await getToken();
      const response = await fetch(`/api/admin/plans/${selectedPlan.id}`, {
        method: 'PATCH',
        headers: jsonAuthHeaders(token),
        body: JSON.stringify({
          title: draft.title,
          summary: draft.summary || null,
          body: draft.body,
          planType: draft.planType,
        }),
      });
      const data = await response.json();
      if (!response.ok) {
        throw new Error(data.error || 'Failed to save plan');
      }

      setPlans((current) => current.map((plan) => plan.id === data.plan.id ? data.plan : plan));
      setNotice('Plan saved. Approval was reset so the current version can be reviewed.');
    } catch (err) {
      console.error('Error saving plan:', err);
      setError(err instanceof Error ? err.message : 'Failed to save plan');
    } finally {
      setIsSaving(false);
    }
  }

  async function createPlan() {
    if (!draft || !canSaveDraft) return;

    setIsSaving(true);
    setError(null);
    setNotice(null);
    try {
      const token = await getToken();
      const response = await fetch('/api/admin/plans', {
        method: 'POST',
        headers: jsonAuthHeaders(token),
        body: JSON.stringify({
          title: draft.title,
          summary: draft.summary || null,
          body: draft.body,
          planType: draft.planType,
        }),
      });
      const data = await response.json();
      if (!response.ok) {
        throw new Error(data.error || 'Failed to create plan');
      }

      setPlans((current) => [data.plan, ...current.filter((plan) => plan.id !== data.plan.id)]);
      setSelectedPlanId(data.plan.id);
      setIsCreatingNew(false);
      setDraft(makeDraft(data.plan));
      setNotice('Plan created.');
    } catch (err) {
      console.error('Error creating plan:', err);
      setError(err instanceof Error ? err.message : 'Failed to create plan');
    } finally {
      setIsSaving(false);
    }
  }

  async function approveSelectedPlan() {
    if (!selectedPlan) return;
    await postPlanAction(selectedPlan.id, 'approve', setIsApproving, 'Plan approved.');
  }

  async function moveSelectedPlanToDraft() {
    if (!selectedPlan) return;
    await postPlanAction(selectedPlan.id, 'unapprove', setIsApproving, 'Plan moved back to draft.');
  }

  async function markSelectedPlanNeedsClarification() {
    if (!selectedPlan) return;
    setIsApproving(true);
    setError(null);
    setNotice(null);
    try {
      const token = await getToken();
      const response = await fetch(`/api/admin/plans/${selectedPlan.id}/needs-clarification`, {
        method: 'POST',
        headers: jsonAuthHeaders(token),
        body: JSON.stringify({ body: noteDraft }),
      });
      const data = await response.json();
      if (!response.ok) {
        throw new Error(data.error || 'Failed to mark plan as needing clarification');
      }

      setPlans((current) => current.map((plan) => plan.id === data.plan.id ? data.plan : plan));
      setNoteDraft('');
      setNotice('Clarification request added to the plan conversation.');
    } catch (err) {
      console.error('Error marking plan as needing clarification:', err);
      setError(err instanceof Error ? err.message : 'Failed to mark plan as needing clarification');
    } finally {
      setIsApproving(false);
    }
  }

  async function postPlanAction(
    planId: string,
    action: 'approve' | 'unapprove',
    setBusy: (value: boolean) => void,
    successMessage: string
  ) {
    setBusy(true);
    setError(null);
    setNotice(null);
    try {
      const token = await getToken();
      const response = await fetch(`/api/admin/plans/${planId}/${action}`, {
        method: 'POST',
        headers: authHeaders(token),
      });
      const data = await response.json();
      if (!response.ok) {
        throw new Error(data.error || `Failed to ${action} plan`);
      }

      setPlans((current) => current.map((plan) => plan.id === data.plan.id ? data.plan : plan));
      setNotice(successMessage);
    } catch (err) {
      console.error(`Error ${action} plan:`, err);
      setError(err instanceof Error ? err.message : `Failed to ${action} plan`);
    } finally {
      setBusy(false);
    }
  }

  function removePlanFromList(planId: string) {
    const deletedIndex = plans.findIndex((plan) => plan.id === planId);
    const remaining = plans.filter((plan) => plan.id !== planId);
    const nextIndex = deletedIndex >= 0 ? Math.min(deletedIndex, remaining.length - 1) : 0;
    setPlans(remaining);
    setSelectedPlanId(remaining[nextIndex]?.id ?? null);
    setDraft(null);
    setNoteDraft('');
  }

  async function deletePlan(plan: AdminPlan) {
    setPlanPendingDelete(null);
    setIsDeleting(true);
    setError(null);
    setNotice(null);
    try {
      const token = await getToken();
      const response = await fetch(`/api/admin/plans/${plan.id}`, {
        method: 'DELETE',
        headers: authHeaders(token),
      });

      if (!response.ok) {
        if (response.status === 404) {
          removePlanFromList(plan.id);
          setDeletedPlanForUndo(null);
          setNotice('This plan was already removed.');
          return;
        }
        let message = 'Failed to delete plan';
        try {
          const data = await response.json();
          message = data.error || message;
        } catch {
          // No response body is expected on success; keep the generic error if parsing fails.
        }
        throw new Error(message);
      }

      removePlanFromList(plan.id);
      setDeletedPlanForUndo(plan);
      setNotice('Plan deleted.');
    } catch (err) {
      console.error('Error deleting plan:', err);
      setError(err instanceof Error ? err.message : 'Failed to delete plan');
    } finally {
      setIsDeleting(false);
    }
  }

  async function restoreDeletedPlan() {
    if (!deletedPlanForUndo) return;

    setIsRestoringDeletedPlan(true);
    setError(null);
    try {
      const token = await getToken();
      const response = await fetch(`/api/admin/plans/${deletedPlanForUndo.id}/restore`, {
        method: 'POST',
        headers: authHeaders(token),
      });
      const data = await response.json();
      if (!response.ok) {
        throw new Error(data.error || 'Failed to restore plan');
      }

      const restoredPlan = data.plan as AdminPlan;
      const isVisibleWithCurrentFilters = statusFilters.length === 0 || statusFilters.includes(restoredPlan.status);
      if (isVisibleWithCurrentFilters) {
        setPlans((current) => [restoredPlan, ...current.filter((plan) => plan.id !== restoredPlan.id)]);
        setSelectedPlanId(restoredPlan.id);
      }
      setDeletedPlanForUndo(null);
      setNotice(isVisibleWithCurrentFilters
        ? 'Plan restored.'
        : 'Plan restored. Clear filters to see it.');
    } catch (err) {
      console.error('Error restoring plan:', err);
      setError(err instanceof Error ? err.message : 'Failed to restore plan');
    } finally {
      setIsRestoringDeletedPlan(false);
    }
  }

  async function addNote() {
    if (!selectedPlan || !noteDraft.trim()) return;

    setIsAddingNote(true);
    setError(null);
    setNotice(null);
    try {
      const token = await getToken();
      const response = await fetch(`/api/admin/plans/${selectedPlan.id}/notes`, {
        method: 'POST',
        headers: jsonAuthHeaders(token),
        body: JSON.stringify({ body: noteDraft }),
      });
      const data = await response.json();
      if (!response.ok) {
        throw new Error(data.error || 'Failed to add note');
      }

      setPlans((current) => current.map((plan) => plan.id === selectedPlan.id
        ? { ...plan, notes: [data.note, ...(plan.notes ?? [])] }
        : plan));
      setNoteDraft('');
      setNotice('Note added to the plan.');
    } catch (err) {
      console.error('Error adding plan note:', err);
      setError(err instanceof Error ? err.message : 'Failed to add note');
    } finally {
      setIsAddingNote(false);
    }
  }

  return (
    <div className="flex h-[calc(100vh-56px)] flex-col overflow-hidden p-6">
      <div className="mb-6 flex flex-wrap items-start justify-between gap-4 shrink-0">
        <div>
          <h1 className="text-2xl font-display font-medium text-text">Plans</h1>
          <p className="mt-1 text-sm text-text-dim">
            Review, edit, approve, and track notes and implementation status for the product plans.
          </p>
        </div>
        <div className="flex items-center gap-2">
          <button type="button" onClick={startNewPlan} className="btn btn-accent btn-sm">
            New plan
          </button>
          <button type="button" onClick={() => void loadPlans()} className="btn btn-sm" disabled={isLoading}>
            Refresh
          </button>
        </div>
      </div>

      {error && (
        <div className="mb-4 rounded-lg border border-red-500/20 bg-red-500/10 px-4 py-3 text-sm text-red-700">
          {error}
        </div>
      )}
      {notice && (
        <div className="mb-4 flex flex-wrap items-center justify-between gap-3 rounded-lg border border-ok/20 bg-ok/10 px-4 py-3 text-sm text-ok">
          <span>{notice}</span>
          {deletedPlanForUndo && notice === 'Plan deleted.' && (
            <button
              type="button"
              onClick={() => void restoreDeletedPlan()}
              className="btn btn-sm"
              disabled={isRestoringDeletedPlan}
            >
              {isRestoringDeletedPlan ? 'Restoring...' : 'Undo'}
            </button>
          )}
        </div>
      )}

      <div className="mb-4 flex flex-wrap items-center gap-3 shrink-0">
        <div className="flex flex-wrap items-center gap-2">
          {STATUS_FILTERS.map((filter) => (
            <label
              key={filter.value}
              className={`flex cursor-pointer items-center gap-2 rounded-full border px-3 py-1.5 text-xs font-semibold transition-colors ${
                statusFilters.includes(filter.value)
                  ? 'border-text bg-text text-bg'
                  : 'border-line bg-panel text-text-dim hover:border-text/40 hover:text-text'
              }`}
            >
              <input
                type="checkbox"
                checked={statusFilters.includes(filter.value)}
                onChange={() => toggleStatusFilter(filter.value)}
                className="h-3.5 w-3.5 accent-current"
              />
              {filter.label}
            </label>
          ))}
        </div>
        {statusFilters.length > 0 && (
          <button
            type="button"
            onClick={() => setStatusFilters([])}
            className="btn btn-sm"
          >
            Clear filters
          </button>
        )}
        <span className="text-xs text-text-faint">
          {plans.length} visible plan{plans.length === 1 ? '' : 's'}
          {statusFilters.length === 0 ? ' across all statuses' : ''}
        </span>
      </div>

      <div className="grid min-h-0 flex-1 grid-cols-[minmax(280px,360px)_minmax(0,1fr)] gap-5">
        <div className="flex min-h-0 flex-col overflow-hidden rounded-lg border border-line bg-panel">
          <div className="border-b border-line px-4 py-3 text-[11px] font-mono font-semibold uppercase text-text-faint">
            Plan queue
          </div>
          <div className="min-h-0 flex-1 overflow-auto">
            {isLoading ? (
              <div className="p-4 text-sm text-text-dim">Loading plans...</div>
            ) : plans.length === 0 ? (
              <div className="p-4 text-sm text-text-dim">No plans match this filter.</div>
            ) : plans.map((plan) => (
              <button
                key={plan.id}
                type="button"
                onClick={() => {
                  setIsCreatingNew(false);
                  setSelectedPlanId(plan.id);
                }}
                className={`block w-full border-b border-line px-4 py-3 text-left transition-colors last:border-b-0 ${
                  !isCreatingNew && plan.id === selectedPlanId ? 'bg-accent/10' : 'hover:bg-bg'
                }`}
              >
                <div className="flex items-start justify-between gap-3">
                  <div className="min-w-0">
                    <div className="text-sm font-semibold text-text">{plan.title}</div>
                    <div className="mt-1 truncate text-xs text-text-faint">{plan.sourceId}</div>
                  </div>
                  <span className={`shrink-0 rounded-full border px-2 py-0.5 text-[10px] font-semibold capitalize ${statusClass(plan.status)}`}>
                    {labelize(plan.status)}
                  </span>
                </div>
                {plan.summary && (
                  <p className="mt-2 line-clamp-2 text-xs leading-5 text-text-dim">{plan.summary}</p>
                )}
                <div className="mt-3 flex flex-wrap gap-2 text-[10px] uppercase tracking-wide text-text-faint">
                  <span>{labelize(plan.planType)}</span>
                  <span>|</span>
                  <span>{labelize(plan.implementationStatus)}</span>
                </div>
              </button>
            ))}
          </div>
        </div>

        <div className="min-h-0 min-w-0 overflow-auto pr-1">
          {!draft || (!selectedPlan && !isCreatingNew) ? (
            <div className="rounded-lg border border-line bg-panel p-6 text-sm text-text-dim">
              Select a plan to review or edit.
            </div>
          ) : (
            <div className="space-y-5">
              {!isCreatingNew && selectedPlan && (
              <section className="rounded-lg border border-line bg-panel p-5">
                <div className="mb-5 flex flex-wrap items-start justify-between gap-3">
                  <div>
                    <div className="text-[10px] font-mono font-semibold uppercase text-text-faint">
                      Task status & conversation
                    </div>
                    <h2 className="mt-1 text-xl font-display font-medium text-text">{selectedPlan.title}</h2>
                  </div>
                  <div className="flex flex-wrap items-center gap-2">
                    <StatusBadge label="Plan" value={selectedPlan.status} />
                    <StatusBadge label="Implementation" value={selectedPlan.implementationStatus} />
                    {selectedPlan.latestJob && (
                      <StatusBadge label="Job" value={selectedPlan.latestJob.status} />
                    )}
                  </div>
                </div>

                <div className="grid grid-cols-1 gap-4 xl:grid-cols-[minmax(0,1fr)_minmax(260px,340px)]">
                  <div className="space-y-4">
                    <div className="rounded-lg border border-line bg-bg p-4">
                      <div className="grid gap-3 text-sm sm:grid-cols-2 lg:grid-cols-4">
                        <div>
                          <div className="text-xs text-text-faint">Plan status</div>
                          <div className={`mt-1 inline-flex rounded-full border px-2 py-0.5 text-[10px] font-semibold capitalize ${statusClass(selectedPlan.status)}`}>
                            {labelize(selectedPlan.status)}
                          </div>
                        </div>
                        <div>
                          <div className="text-xs text-text-faint">Implementation</div>
                          <div className={`mt-1 inline-flex rounded-full border px-2 py-0.5 text-[10px] font-semibold capitalize ${statusClass(selectedPlan.implementationStatus)}`}>
                            {labelize(selectedPlan.implementationStatus)}
                          </div>
                        </div>
                        <div>
                          <div className="text-xs text-text-faint">Approved</div>
                          <div className="mt-1 text-text">{formatShortDateTime(selectedPlan.approvedAt)}</div>
                        </div>
                        <div>
                          <div className="text-xs text-text-faint">Latest job</div>
                          <div className="mt-1 text-text">
                            {selectedPlan.latestJob
                              ? `${labelize(selectedPlan.latestJob.status)} / ${labelize(selectedPlan.latestJob.implementationStatus)}`
                              : 'No job yet'}
                          </div>
                        </div>
                      </div>

                      {selectedPlan.approvedByUserId && (
                        <div className="mt-4 text-xs text-text-dim">
                          Approved by <span className="break-all text-text">{selectedPlan.approvedByUserId}</span>
                        </div>
                      )}

                      {selectedPlan.implementationNotes && (
                        <div className="mt-4 rounded-lg border border-line bg-panel p-3 text-xs leading-5 text-text-dim">
                          {selectedPlan.implementationNotes}
                        </div>
                      )}

                      {selectedPlan.latestJob && (
                        <div className="mt-4 grid gap-3 text-sm md:grid-cols-2">
                          <div>
                            <div className="text-xs text-text-faint">Trigger</div>
                            <div className="mt-1 capitalize text-text">{labelize(selectedPlan.latestJob.trigger)}</div>
                          </div>
                          <div>
                            <div className="text-xs text-text-faint">Completed</div>
                            <div className="mt-1 text-text">{formatShortDateTime(selectedPlan.latestJob.completedAt)}</div>
                          </div>
                          {selectedPlan.latestJob.resultSummary && (
                            <div className="rounded-lg border border-line bg-panel p-3 text-xs leading-5 text-text-dim md:col-span-2">
                              {selectedPlan.latestJob.resultSummary}
                            </div>
                          )}
                          {selectedPlan.latestJob.errorMessage && (
                            <div className="rounded-lg border border-red-500/20 bg-red-500/10 p-3 text-xs leading-5 text-red-700 md:col-span-2">
                              {selectedPlan.latestJob.errorMessage}
                            </div>
                          )}
                        </div>
                      )}
                    </div>

                    <div>
                      <div className="mb-3 flex items-center justify-between gap-3">
                        <div className="text-[10px] font-mono font-semibold uppercase text-text-faint">
                          Notes & conversation
                        </div>
                        <span className="text-[11px] text-text-faint">
                          {selectedPlan.notes.length}
                        </span>
                      </div>

                      {selectedPlan.notes.length > 0 ? (
                        <div className="space-y-3">
                          {[...selectedPlan.notes].reverse().map((note) => (
                            <article key={note.id} className="rounded-lg border border-line bg-bg p-3">
                              <div className="flex items-start justify-between gap-2">
                                <div>
                                  <span className={`rounded-full border px-2 py-0.5 text-[10px] font-semibold capitalize ${statusClass(note.noteType === 'execution' ? 'running' : 'draft')}`}>
                                    {labelize(note.noteType)}
                                  </span>
                                  <div className="mt-2 text-[11px] text-text-faint">
                                    {formatShortDateTime(note.createdAt)}
                                  </div>
                                </div>
                                <div className="min-w-0 text-right text-[11px] text-text-faint">
                                  {note.authorUserId ? (
                                    <span className="block truncate">{note.authorUserId}</span>
                                  ) : (
                                    <span>System</span>
                                  )}
                                  {note.jobId && (
                                    <span className="mt-1 block font-mono">job {note.jobId.slice(0, 8)}</span>
                                  )}
                                </div>
                              </div>
                              <p className="mt-3 whitespace-pre-wrap text-sm leading-5 text-text-dim">
                                {note.body}
                              </p>
                            </article>
                          ))}
                        </div>
                      ) : (
                        <div className="rounded-lg border border-line bg-bg p-3 text-sm text-text-dim">
                          No conversation yet.
                        </div>
                      )}
                    </div>

                    <div className="rounded-lg border border-line bg-bg p-4">
                      <div className="text-[10px] font-mono font-semibold uppercase text-text-faint">Reply</div>
                      <div className="mt-3 space-y-2">
                        <textarea
                          aria-label="Plan note"
                          value={noteDraft}
                          onChange={(event) => setNoteDraft(event.target.value)}
                          className="input min-h-[116px] w-full resize-y text-sm leading-5"
                          placeholder="Continue the plan conversation, or write the question Codex should answer..."
                        />
                        <div className="flex justify-end">
                          <button
                            type="button"
                            onClick={() => void addNote()}
                            className="btn btn-sm"
                            disabled={isAddingNote || !noteDraft.trim()}
                          >
                            {isAddingNote ? 'Adding...' : 'Add reply'}
                          </button>
                        </div>
                      </div>
                    </div>
                  </div>

                  <div className="space-y-4">
                    <div className="rounded-lg border border-line bg-bg p-4">
                      <div className="text-[10px] font-mono font-semibold uppercase text-text-faint">Actions</div>
                      <p className="mt-2 text-xs leading-5 text-text-dim">
                        Use clarification when the plan needs a Codex discussion. Codex should answer in this plan conversation before it is approved.
                      </p>
                      <div className="mt-4 flex flex-col gap-2">
                        {selectedPlan.status !== 'approved' && (
                          <button
                            type="button"
                            onClick={() => void approveSelectedPlan()}
                            className="btn btn-accent btn-sm"
                            disabled={isApproving || hasChanges || !canChangeSelectedPlanStatus}
                          >
                            {isApproving ? 'Updating...' : 'Approve plan'}
                          </button>
                        )}
                        {selectedPlan.status !== 'needs_clarification' && (
                          <button
                            type="button"
                            onClick={() => void markSelectedPlanNeedsClarification()}
                            className="btn btn-sm"
                            disabled={isApproving || hasChanges || !canRequestClarification}
                          >
                            Needs clarification
                          </button>
                        )}
                        {selectedPlan.status !== 'draft' && (
                          <button
                            type="button"
                            onClick={() => void moveSelectedPlanToDraft()}
                            className="btn btn-sm"
                            disabled={isApproving || hasChanges || !canChangeSelectedPlanStatus}
                          >
                            Move back to draft
                          </button>
                        )}
                        <button
                          type="button"
                          onClick={() => setPlanPendingDelete(selectedPlan)}
                          className="btn btn-sm border-red-500/60 text-red-600 hover:bg-red-500/10 focus-visible:ring-2 focus-visible:ring-red-500 focus-visible:ring-offset-2 disabled:cursor-not-allowed disabled:border-red-500/15 disabled:text-red-600/40 disabled:hover:bg-transparent"
                          disabled={isDeleting || isApproving || Boolean(deleteDisabledReason) || !canChangeSelectedPlanStatus}
                        >
                          {isDeleting ? 'Deleting...' : 'Delete plan'}
                        </button>
                        {deleteDisabledReason && (
                          <div className="text-xs text-text-faint">{deleteDisabledReason}</div>
                        )}
                        {hasChanges && (
                          <div className="text-xs text-text-faint">Save changes before changing status.</div>
                        )}
                        {!hasChanges && selectedPlan.status !== 'needs_clarification' && !noteDraft.trim() && (
                          <div className="text-xs text-text-faint">
                            Write the clarification request in Reply first.
                          </div>
                        )}
                      </div>
                    </div>

                  </div>
                </div>
              </section>
              )}

              <section className="rounded-lg border border-line bg-panel p-5">
                <div className="mb-5 flex flex-wrap items-start justify-between gap-3">
                  <div>
                    <div className="text-[10px] font-mono font-semibold uppercase text-text-faint">
                      Editor
                    </div>
                    <h2 className="mt-1 text-xl font-display font-medium text-text">
                      {isCreatingNew ? 'New plan' : selectedPlan?.title}
                    </h2>
                  </div>
                  <div className="flex items-center gap-2">
                    <button
                      type="button"
                      onClick={() => {
                        if (isCreatingNew) {
                          cancelNewPlan();
                          return;
                        }
                        if (selectedPlan) setDraft(makeDraft(selectedPlan));
                      }}
                      className="btn btn-sm"
                      disabled={isSaving || (!isCreatingNew && !hasChanges)}
                    >
                      {isCreatingNew ? 'Cancel' : 'Reset'}
                    </button>
                    <button
                      type="button"
                      onClick={() => void savePlan()}
                      className="btn btn-accent btn-sm"
                      disabled={isSaving || !canSaveDraft || (!isCreatingNew && !hasChanges)}
                    >
                      {isSaving ? 'Saving...' : isCreatingNew ? 'Create plan' : 'Save'}
                    </button>
                  </div>
                </div>

                {isCreatingNew ? (
                  <div className="mb-4 rounded-lg border border-line bg-bg px-3 py-2 text-xs text-text-dim">
                    Create a draft plan here, then use the conversation above it to refine and approve it.
                  </div>
                ) : hasChanges && (
                  <div className="mb-4 rounded-lg border border-warn/20 bg-warn/10 px-3 py-2 text-xs text-warn">
                    Saving edits moves this plan back to draft so the edited version can be approved deliberately.
                  </div>
                )}

                <div className="space-y-4">
                  <label className="block">
                    <span className="mb-1 block text-xs font-semibold uppercase text-text-faint">Title</span>
                    <input
                      value={draft.title}
                      onChange={(event) => setDraft({ ...draft, title: event.target.value })}
                      className="input w-full"
                    />
                  </label>

                  <label className="block">
                    <span className="mb-1 block text-xs font-semibold uppercase text-text-faint">Type</span>
                    <select
                      value={draft.planType}
                      onChange={(event) => setDraft({ ...draft, planType: event.target.value as AdminPlanType })}
                      className="input w-full"
                    >
                      <option value="product">Product</option>
                      <option value="external_harness">External harness</option>
                      <option value="manual">Manual</option>
                    </select>
                  </label>

                  <label className="block">
                    <span className="mb-1 block text-xs font-semibold uppercase text-text-faint">Summary</span>
                    <textarea
                      value={draft.summary}
                      onChange={(event) => setDraft({ ...draft, summary: event.target.value })}
                      className="input min-h-[88px] w-full resize-y"
                    />
                  </label>

                  <label className="block">
                    <span className="mb-1 block text-xs font-semibold uppercase text-text-faint">Plan body</span>
                    <textarea
                      value={draft.body}
                      onChange={(event) => setDraft({ ...draft, body: event.target.value })}
                      className="input min-h-[420px] w-full resize-y font-mono text-[13px] leading-6"
                    />
                  </label>
                </div>
              </section>
            </div>
          )}
        </div>
      </div>
      {planPendingDelete && (
        <div
          className="fixed inset-0 z-50 grid place-items-center bg-black/50 p-4"
          onMouseDown={(event) => {
            if (event.target === event.currentTarget) {
              setPlanPendingDelete(null);
            }
          }}
        >
          <div
            role="alertdialog"
            aria-modal="true"
            aria-labelledby="delete-plan-title"
            aria-describedby="delete-plan-description"
            className="w-full max-w-lg rounded-lg border border-line bg-panel p-5 shadow-xl"
          >
            <h2 id="delete-plan-title" className="text-xl font-display font-medium text-text">
              Delete this plan?
            </h2>
            <p id="delete-plan-description" className="mt-3 text-sm leading-6 text-text-dim">
              It will be removed from the admin queue. Notes and job history are retained, and you can undo this after deletion.
            </p>
            <div className="mt-4 rounded-lg border border-line bg-bg p-3 text-sm font-medium text-text">
              {planPendingDelete.title}
            </div>
            <div className="mt-5 flex justify-end gap-2">
              <button
                type="button"
                className="btn btn-sm"
                autoFocus
                onClick={() => setPlanPendingDelete(null)}
                disabled={isDeleting}
              >
                Cancel
              </button>
              <button
                type="button"
                className="btn btn-sm border-red-600 bg-red-600 text-white hover:bg-red-700 disabled:cursor-not-allowed disabled:border-red-500/30 disabled:bg-red-500/30"
                onClick={() => void deletePlan(planPendingDelete)}
                disabled={isDeleting}
              >
                {isDeleting ? 'Deleting...' : 'Delete plan'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

function StatusBadge({ label, value }: { label: string; value: string }) {
  return (
    <div className="flex items-center gap-2 rounded-full border border-line bg-bg px-2.5 py-1">
      <span className="text-[10px] font-mono font-semibold uppercase text-text-faint">{label}</span>
      <span className={`rounded-full border px-2 py-0.5 text-[10px] font-semibold capitalize ${statusClass(value)}`}>
        {labelize(value)}
      </span>
    </div>
  );
}
